import { cn } from '@foundation/utils/cn';
import { ReactNode } from 'react';

import {
  HeadlessTextInput,
  HeadlessTextInputProps,
  HeadlessTextInputStyles,
} from '@design-system/headless-ui';
import { CheckCircle, Close } from '@design-system/icons';

import { Spinner } from './Spinner';

export type TextInputProps = Omit<
  HeadlessTextInputProps,
  | 'className'
  | 'size'
  | 'styles'
  | 'endSlot'
  | 'startSlot'
  | 'renderLoadingIndicator'
  | 'renderClearIcon'
  | 'renderSuccessIndicator'
> & {
  readonly startAdornment?: ReactNode;
  readonly endAdornment?: ReactNode;
  readonly 'data-testid'?: string;
};

export function getTextInputStyles({
  label,
  hasStartSlot = false,
  hasEndSlot,
  hasPlaceholder = false,
}: {
  readonly label?: ReactNode;
  readonly hasStartSlot?: boolean;
  readonly hasEndSlot: boolean;
  readonly hasPlaceholder?: boolean;
}): HeadlessTextInputStyles {
  return {
    label: cn(
      'text-md leading-6 tracking-tightest text-pmui-fg-placeholder duration-100',
      'data-filled:text-xs data-filled:leading-3 data-filled:tracking-tightest data-filled:text-pmui-fg-secondary',
      'data-focused:text-xs data-focused:leading-3 data-focused:tracking-tightest data-focused:text-pmui-fg-secondary',
      'data-disabled:text-pmui-interactive-fg-disabled',
      hasStartSlot && 'left-10',
      // A native placeholder already occupies the resting position, so the label stays
      // invisible until the field is filled, then snaps into its small size with no transition.
      hasPlaceholder &&
        'top-2 translate-y-0 text-xs leading-3 tracking-tightest text-pmui-fg-secondary opacity-0 duration-0 data-filled:opacity-100',
    ),
    requiredIndicator: 'text-pmui-fg-status-negative-default',
    control: cn(
      'h-14 rounded-md border border-pmui-border-primary bg-pmui-bg-primary px-4 text-md leading-6 tracking-tightest text-pmui-fg-primary shadow-sm',
      'placeholder:text-pmui-fg-placeholder',
      'focus:outline-hidden',
      // :disabled also matches :read-only, so this covers both.
      'not-read-only:focus:border-pmui-border-focus-ring-focus',
      'not-read-only:focus:ring-1 not-read-only:focus:ring-inset not-read-only:focus:ring-pmui-border-focus-ring-focus',
      'disabled:cursor-not-allowed disabled:bg-pmui-interactive-bg-disabled disabled:text-pmui-interactive-fg-disabled disabled:shadow-none',
      'read-only:not-disabled:cursor-default',
      'data-invalid:border-pmui-border-status-negative data-invalid:shadow-none',
      'data-invalid:ring-1 data-invalid:ring-inset data-invalid:ring-pmui-border-focus-ring-focus-negative',
      // With a native placeholder, the label only reserves space once filled, so the
      // empty placeholder state stays centered exactly like an empty label-only input.
      label && (hasPlaceholder ? 'data-filled:pt-4' : 'pt-4'),
      hasStartSlot && 'pl-10',
      hasEndSlot && 'pr-10',
    ),
    helperText: 'text-sm leading-5 tracking-tightest text-pmui-fg-secondary',
    error:
      'text-sm leading-5 tracking-tightest text-pmui-fg-status-negative-default',
    clearButton: cn(
      'rounded-full text-pmui-fg-muted hover:text-pmui-fg-primary',
      'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-pmui-border-focus-ring-focus focus-visible:ring-offset-2',
    ),
  };
}

function TextInputComponent({
  label,
  startAdornment,
  endAdornment,
  'data-testid': dataTestId,
  ...props
}: TextInputProps) {
  const hasEndSlot = Boolean(
    props.isLoading ||
      (props.clearable &&
        props.onClear &&
        !props.disabled &&
        !props.readOnly) ||
      (props.success && !props.error) ||
      endAdornment,
  );
  const hasPlaceholder = Boolean(props.placeholder);

  return (
    <HeadlessTextInput
      {...props}
      label={label}
      data-testid={dataTestId}
      startSlot={startAdornment}
      endSlot={endAdornment}
      renderLoadingIndicator={() => <Spinner variant="dots" />}
      renderClearIcon={() => <Close size={20} />}
      renderSuccessIndicator={() => (
        <CheckCircle
          size={20}
          className="text-pmui-fg-status-positive-default"
        />
      )}
      styles={getTextInputStyles({
        label,
        hasStartSlot: Boolean(startAdornment),
        hasEndSlot,
        hasPlaceholder,
      })}
    />
  );
}

export const TextInput = TextInputComponent;
